import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocale } from '@/lib/i18n';
import {
  REGION_TO_CURRENCY,
  isSupportedCurrency,
} from './currency-metadata';
import { getInitialRatesState, refreshRates, type RatesState } from './currency-rates';
import {
  loadPreferredCurrency,
  loadRecentCurrencies,
  pushRecentCurrency,
  savePreferredCurrency,
} from './currency-storage';

interface CurrencyContextValue {
  currency: string;
  setCurrency: (code: string) => void;
  recent: string[];
  rates: Record<string, number> | null;
  status: RatesState['status'];
  updatedAt: RatesState['updatedAt'];
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within a CurrencyProvider');
  return ctx;
}

function regionFromTag(tag: string): string | null {
  const parts = tag.split(/[-_]/);
  if (parts.length < 2) return null;
  const region = parts[parts.length - 1].toUpperCase();
  return /^[A-Z]{2}$/.test(region) ? region : null;
}

/**
 * Sync, non-blocking default: saved preference -> browser region currency ->
 * locale region currency -> USD. Every step is guarded and validated.
 */
function resolveDefaultCurrency(locale: string): string {
  try {
    const saved = loadPreferredCurrency();
    if (saved && isSupportedCurrency(saved)) return saved.toUpperCase();
  } catch {
    /* fall through to region default */
  }
  const tags: string[] = [];
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
      tags.push(navigator.language);
    }
  } catch {
    /* navigator unavailable */
  }
  tags.push(locale);
  for (const tag of tags) {
    try {
      const region = regionFromTag(tag);
      if (!region) continue;
      const code = REGION_TO_CURRENCY[region];
      if (code && isSupportedCurrency(code)) return code;
    } catch {
      continue;
    }
  }
  return 'USD';
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [currency, setCurrencyState] = useState<string>(() => resolveDefaultCurrency(locale));
  const [recent, setRecent] = useState<string[]>(() => {
    try {
      return loadRecentCurrencies();
    } catch {
      return [];
    }
  });
  const [ratesState, setRatesState] = useState<RatesState>(() => getInitialRatesState());

  useEffect(() => {
    if (ratesState.status === 'LOADING' || ratesState.status === 'STALE') {
      let cancelled = false;
      refreshRates().then((next) => {
        if (!cancelled) setRatesState(next);
      });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [ratesState.status]);

  const setCurrency = useCallback((code: string) => {
    const upper = code.toUpperCase();
    if (!isSupportedCurrency(upper)) return;
    setCurrencyState(upper);
    try {
      savePreferredCurrency(upper);
    } catch {
      /* persistence is best-effort */
    }
    try {
      setRecent(pushRecentCurrency(upper));
    } catch {
      /* recents are best-effort */
    }
  }, []);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      setCurrency,
      recent,
      rates: ratesState.rates,
      status: ratesState.status,
      updatedAt: ratesState.updatedAt,
    }),
    [currency, setCurrency, recent, ratesState],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
